/**
 * Partner Match — "Encuentra tu partner perfecto"
 * Quiz de compatibilidad + Swipe cards. Celebración animada en cada match.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Heart, X, Sparkle, UsersThree, ChatCircle, MapPin, Trophy,
  CaretLeft, CaretRight, ArrowClockwise, HandWaving, GameController,
} from "@phosphor-icons/react";
import confetti from "canvas-confetti";
import { useAuth } from "@/context/AuthContext";
import MatchChatModal from "@/components/MatchChatModal";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Resolve the best profile photo for a user object.
// Priority: uploaded photo_path (served via API) > photo_url > profile_photo_url > avatar_url
function resolvePhoto(u) {
  if (!u) return null;
  if (u.photo_path) return `${API_URL}/api/users/photo/${u.photo_path}`;
  return u.photo_url || u.profile_photo_url || u.avatar_url || null;
}

const BG = "bg-[#0A0A0A]";

// --- Quiz dialog ---
function QuizDialog({ open, onClose, questions, initial, onSave }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(initial || {});
  useEffect(() => { if (open) { setIdx(0); setAnswers(initial || {}); } }, [open, initial]);
  if (!open) return null;

  const q = questions[idx];
  const total = questions.length;
  const value = answers[q.key] ?? "";
  const canNext = value !== "" && value !== null && value !== undefined;

  const handleNext = async () => {
    if (idx < total - 1) { setIdx(idx + 1); return; }
    // last — save
    try {
      await api.post("/partner-match/me", answers);
      toast.success("Quiz guardado! Tus matches se recalcularon.");
      onSave?.(answers);
      onClose();
    } catch (e) {
      toast.error("No se pudo guardar el quiz");
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="pm-quiz-overlay">
      <div className="w-full max-w-lg bg-gradient-to-br from-[#1A0F2E] via-[#2D1B4E] to-[#0A0A0A] border-2 border-[#CCFF00]/40 rounded-2xl p-5 sm:p-7 shadow-[0_30px_80px_rgba(204,255,0,0.2)] relative animate-fade-in" data-testid="pm-quiz-dialog">
        {/* Progress */}
        <div className="flex items-center gap-1 mb-4">
          {questions.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full ${i <= idx ? "bg-[#CCFF00]" : "bg-white/10"}`} />
          ))}
        </div>
        <p className="text-xs tracking-[0.3em] uppercase text-[#CCFF00] font-bold mb-1">
          Pregunta {idx + 1} de {total}
        </p>
        <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase text-white leading-tight mb-5">
          {q.label}
        </h2>

        {q.type === "text" ? (
          <Input
            autoFocus
            placeholder={q.placeholder}
            value={value}
            onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
            className="bg-black/40 border-white/20 text-white text-lg"
            data-testid={`pm-quiz-${q.key}-input`}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {q.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setAnswers((a) => ({ ...a, [q.key]: opt.value }))}
                className={`relative p-3 rounded-xl border-2 transition-all text-left group ${
                  value === opt.value
                    ? "border-[#CCFF00] bg-[#CCFF00]/10 shadow-[0_0_20px_rgba(204,255,0,0.3)]"
                    : "border-white/10 bg-black/30 hover:border-[#CCFF00]/40 hover:bg-white/5"
                }`}
                data-testid={`pm-quiz-${q.key}-${opt.value}`}
              >
                <div className="text-3xl mb-1">{opt.emoji}</div>
                <div className={`text-sm font-bold uppercase tracking-wide ${value === opt.value ? "text-[#CCFF00]" : "text-white"}`}>
                  {opt.label}
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-6 gap-3">
          <Button
            variant="outline"
            disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            className="border-white/20 text-white hover:bg-white/5"
            data-testid="pm-quiz-prev"
          >
            <CaretLeft size={16} /> Atrás
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canNext}
            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide px-5 shadow-lg disabled:opacity-30"
            data-testid="pm-quiz-next"
          >
            {idx < total - 1 ? (<>Siguiente <CaretRight size={16} /></>) : (<>Encontrar matches <Sparkle size={16} weight="fill" /></>)}
          </Button>
        </div>

        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center"
          aria-label="Cerrar"
          data-testid="pm-quiz-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

// --- Swipe card ---
function SwipeCard({ candidate, onSwipe, zIndex, isTop }) {
  const [dragX, setDragX] = useState(0);
  const [exit, setExit] = useState(null); // "right" | "left" | null
  const startX = useRef(null);

  const handlePointerDown = (e) => {
    if (!isTop) return;
    startX.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (startX.current === null) return;
    setDragX(e.clientX - startX.current);
  };
  const handlePointerUp = () => {
    const threshold = 100;
    if (dragX > threshold) triggerSwipe("right");
    else if (dragX < -threshold) triggerSwipe("left");
    else setDragX(0);
    startX.current = null;
  };
  const triggerSwipe = (dir) => {
    setExit(dir);
    setTimeout(() => onSwipe?.(dir, candidate), 320);
  };

  const score = candidate.match_score || 55;
  const scoreColor = score >= 80 ? "#CCFF00" : score >= 65 ? "#FBBF24" : score >= 50 ? "#60A5FA" : "#F87171";

  const rotation = dragX / 20;
  const translateX = exit === "right" ? "150%" : exit === "left" ? "-150%" : `${dragX}px`;
  const opacity = exit ? 0 : 1;

  const scheduleLabel = { morning: "Mañanas", afternoon: "Tardes", evening: "Noches", night: "Madrugadas", flexible: "Flexible" }[candidate.quiz?.schedule];
  const levelLabel = { principiante: "Principiante", intermedio: "Intermedio", avanzado: "Avanzado", pro: "Pro" }[candidate.quiz?.level];
  const handLabel = { diestro: "Diestro", zurdo: "Zurdo", ambidiestro: "Ambidiestro" }[candidate.quiz?.hand];
  const sideLabel = { drive: "Drive", reves: "Revés", ambos: "Drive + Revés" }[candidate.quiz?.side];
  const freqLabel = { monthly: "1x mes", "1_week": "1x semana", "2_week": "2x semana", "3_week": "3x semana", "4plus_week": "4+ semana" }[candidate.quiz?.frequency];
  const yearsLabel = typeof candidate.quiz?.years_playing === "number"
    ? (candidate.quiz.years_playing === 0 ? "< 1 año" : `${candidate.quiz.years_playing}+ años`)
    : null;

  const avatarSrc = resolvePhoto(candidate);
  const initial = (candidate.name || "?").trim().charAt(0).toUpperCase();

  return (
    <div
      className="absolute inset-0 select-none"
      style={{
        zIndex,
        transform: `translateX(${translateX}) rotate(${exit ? (exit === "right" ? 30 : -30) : rotation}deg)`,
        opacity,
        transition: exit ? "transform 300ms ease-out, opacity 300ms ease-out" : (startX.current === null ? "transform 300ms ease-out" : "none"),
        touchAction: "pan-y",
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      data-testid={`pm-card-${candidate.id}`}
    >
      <div className="relative h-full w-full rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
        {/* Photo / avatar */}
        <div className="absolute inset-0">
          {avatarSrc ? (
            <img src={avatarSrc} alt={candidate.name} className="w-full h-full object-cover" draggable={false} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-900 via-fuchsia-900 to-[#CCFF00]/20 flex items-center justify-center">
              <span className="font-['Barlow_Condensed'] text-[180px] font-black text-white/70">{initial}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        {/* Match score badge */}
        <div className="absolute top-4 right-4 px-3 py-2 rounded-xl backdrop-blur-md bg-black/50 border" style={{ borderColor: scoreColor + "80" }}>
          <p className="text-[9px] uppercase tracking-widest text-white/60 font-bold">Match</p>
          <p className="font-['Barlow_Condensed'] text-3xl font-black leading-none" style={{ color: scoreColor }}>{score}%</p>
        </div>

        {/* Swipe direction overlay */}
        {dragX > 40 && !exit && (
          <div className="absolute top-6 left-6 px-4 py-2 rounded-xl bg-emerald-500/90 text-white font-black uppercase tracking-widest text-xl rotate-[-15deg] border-2 border-white shadow-lg">
            ¡ME GUSTA!
          </div>
        )}
        {dragX < -40 && !exit && (
          <div className="absolute top-6 right-6 px-4 py-2 rounded-xl bg-rose-500/90 text-white font-black uppercase tracking-widest text-xl rotate-[15deg] border-2 border-white shadow-lg">
            PASO
          </div>
        )}

        {/* Info */}
        <div className="absolute bottom-0 left-0 right-0 p-3.5 space-y-1.5">
          <div className="flex items-end justify-between gap-2">
            <h3 className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-white leading-none drop-shadow">
              {candidate.name}
            </h3>
            {candidate.category && (
              <span className="px-2 py-0.5 rounded-md bg-[#CCFF00] text-black text-[11px] font-black uppercase">
                Cat {candidate.category}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-white/80">
            {candidate.state && (
              <span className="inline-flex items-center gap-1"><MapPin size={10} /> {candidate.state}</span>
            )}
            {yearsLabel && (
              <span className="inline-flex items-center gap-1"><Trophy size={10} /> {yearsLabel}</span>
            )}
            {candidate.role === "profesor" && (
              <span className="px-1 py-0.5 rounded bg-purple-500/30 border border-purple-400/50 text-purple-200 text-[9px] font-bold uppercase">Pro</span>
            )}
          </div>
          {candidate.has_quiz ? (
            <>
              {/* Key stats row — hand + side + frequency */}
              <div className="grid grid-cols-3 gap-1">
                {handLabel && (
                  <div className="px-1.5 py-1 rounded-md bg-black/55 border border-white/15 text-center">
                    <p className="text-[8px] uppercase tracking-widest text-white/50 font-bold leading-none">Mano</p>
                    <p className="text-[11px] font-black text-white uppercase truncate leading-tight mt-0.5">{handLabel}</p>
                  </div>
                )}
                {sideLabel && (
                  <div className="px-1.5 py-1 rounded-md bg-black/55 border border-[#CCFF00]/30 text-center">
                    <p className="text-[8px] uppercase tracking-widest text-white/50 font-bold leading-none">Lado</p>
                    <p className="text-[11px] font-black text-[#CCFF00] uppercase truncate leading-tight mt-0.5">{sideLabel}</p>
                  </div>
                )}
                {freqLabel && (
                  <div className="px-1.5 py-1 rounded-md bg-black/55 border border-white/15 text-center">
                    <p className="text-[8px] uppercase tracking-widest text-white/50 font-bold leading-none">Frec.</p>
                    <p className="text-[11px] font-black text-white uppercase truncate leading-tight mt-0.5">{freqLabel}</p>
                  </div>
                )}
              </div>
              {/* Secondary pills */}
              <div className="flex flex-wrap gap-1">
                {scheduleLabel && <span className="px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] text-white/90">{scheduleLabel}</span>}
                {levelLabel && <span className="px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] text-white/90">{levelLabel}</span>}
                {candidate.quiz?.style && <span className="px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] text-white/90 capitalize">{candidate.quiz.style}</span>}
                {candidate.quiz?.zone && <span className="px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] text-white/90">📍 {candidate.quiz.zone}</span>}
              </div>
            </>
          ) : (
            <p className="text-[11px] text-amber-300/80">Aún no tomó el quiz · match estimado</p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Match celebration ---
function MatchModal({ matchUser, onClose, onChat }) {
  useEffect(() => {
    if (!matchUser) return;
    const end = Date.now() + 1200;
    const interval = setInterval(() => {
      confetti({ particleCount: 30, startVelocity: 55, spread: 70, origin: { x: Math.random(), y: 0 }, colors: ["#CCFF00", "#FF1493", "#8B5CF6"] });
      if (Date.now() > end) clearInterval(interval);
    }, 180);
    return () => clearInterval(interval);
  }, [matchUser]);
  if (!matchUser) return null;
  const avatar = resolvePhoto(matchUser);
  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/85 backdrop-blur-md p-4" data-testid="pm-match-modal">
      <div className="w-full max-w-md bg-gradient-to-br from-[#4A1B6A] via-[#120020] to-[#0A0A0A] border-2 border-[#CCFF00] rounded-3xl p-6 sm:p-8 text-center shadow-[0_30px_120px_rgba(204,255,0,0.4)] animate-fade-in">
        <p className="text-xs tracking-[0.4em] uppercase text-[#CCFF00] font-black mb-2">¡Es un match!</p>
        <h2 className="font-['Barlow_Condensed'] text-5xl sm:text-6xl font-black uppercase leading-none mb-5">
          <span className="text-white">Partner </span>
          <span className="text-[#CCFF00]">Perfecto</span>
        </h2>
        <div className="relative inline-block mb-4">
          {avatar ? (
            <img src={avatar} alt={matchUser.name} className="w-32 h-32 rounded-full object-cover border-4 border-[#CCFF00] shadow-[0_0_40px_rgba(204,255,0,0.6)]" />
          ) : (
            <div className="w-32 h-32 rounded-full border-4 border-[#CCFF00] bg-gradient-to-br from-purple-700 to-pink-700 flex items-center justify-center font-black text-5xl text-white">
              {(matchUser.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <Heart size={40} weight="fill" className="absolute -right-2 -bottom-2 text-rose-500 drop-shadow-[0_0_12px_rgba(244,63,94,0.8)]" />
        </div>
        <p className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-white mb-4">{matchUser.name}</p>
        <div className="flex items-center gap-3 justify-center">
          <Button onClick={onChat} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide" data-testid="pm-match-chat">
            <ChatCircle size={16} weight="fill" className="mr-1" /> Mándale mensaje
          </Button>
          <Button onClick={onClose} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="pm-match-continue">
            Seguir buscando
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PartnerMatch() {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [myQuiz, setMyQuiz] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(0);
  const [matches, setMatches] = useState([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [matchModal, setMatchModal] = useState(null);
  const [chatWith, setChatWith] = useState(null); // user object for the chat modal
  const [tab, setTab] = useState("discover"); // discover | matches
  const [search, setSearch] = useState("");

  const loadAll = async (searchQ = "") => {
    setLoading(true);
    try {
      const [qRes, meRes, sRes, mRes] = await Promise.all([
        api.get("/partner-match/questions"),
        api.get("/partner-match/me"),
        api.get(`/partner-match/suggestions?limit=30${searchQ ? `&search=${encodeURIComponent(searchQ)}` : ""}`),
        api.get("/partner-match/matches"),
      ]);
      setQuestions(qRes.data.questions || []);
      setMyQuiz(meRes.data.quiz || null);
      setCandidates(sRes.data.candidates || []);
      setMatches(mRes.data.matches || []);
      setCursor(0);
      if (!meRes.data.quiz) setShowQuiz(true);
    } catch (e) {
      toast.error("No se pudo cargar Partner Match");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const doSwipe = async (dir, candidate) => {
    try {
      const r = await api.post("/partner-match/swipe", { target_id: candidate.id, direction: dir });
      setCursor((c) => c + 1);
      if (r.data?.matched && r.data?.match_user) {
        setMatchModal(r.data.match_user);
        api.get("/partner-match/matches").then((res) => setMatches(res.data.matches || []));
      }
    } catch (e) {
      toast.error("Error al registrar swipe");
    }
  };

  const resetSwipes = async () => {
    try {
      await api.post("/partner-match/reset-swipes");
      toast.success("Reiniciado — vuelve a ver todos los perfiles");
      loadAll(search);
    } catch (e) {
      toast.error("No se pudo reiniciar");
    }
  };

  const current = candidates[cursor];
  const next = candidates[cursor + 1];
  const noMore = !current;

  return (
    <div className={`space-y-4 ${BG} min-h-full`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-pink-400 mb-1 flex items-center gap-1">
            <HandWaving size={12} weight="fill" /> Encuentra tu partner
          </p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">
            Partner <span className="text-[#CCFF00]">Match</span>
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowQuiz(true)} className="border-white/20 text-white hover:bg-white/5 text-xs uppercase font-bold" data-testid="pm-retake-quiz">
            <GameController size={14} className="mr-1" /> {myQuiz ? "Rehacer quiz" : "Tomar quiz"}
          </Button>
          <Button variant="outline" onClick={resetSwipes} className="border-white/20 text-white hover:bg-white/5 text-xs uppercase font-bold" data-testid="pm-reset-swipes">
            <ArrowClockwise size={14} className="mr-1" /> Reiniciar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        <button
          onClick={() => setTab("discover")}
          className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${tab === "discover" ? "text-[#CCFF00] border-b-2 border-[#CCFF00]" : "text-white/50 hover:text-white"}`}
          data-testid="pm-tab-discover"
        >
          Descubrir
        </button>
        <button
          onClick={() => setTab("matches")}
          className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${tab === "matches" ? "text-[#CCFF00] border-b-2 border-[#CCFF00]" : "text-white/50 hover:text-white"}`}
          data-testid="pm-tab-matches"
        >
          Mis matches <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px]">{matches.length}</span>
        </button>
      </div>

      {tab === "discover" && (
        <div className="space-y-3">
          {/* Search */}
          <div className="flex items-center gap-2">
            <Input
              placeholder="Buscar jugador por nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadAll(search); }}
              className="bg-black/40 border-white/20 text-white"
              data-testid="pm-search-input"
            />
            <Button variant="outline" onClick={() => loadAll(search)} className="border-white/20 text-white hover:bg-white/5 shrink-0" data-testid="pm-search-btn">
              Buscar
            </Button>
          </div>

          <div className="relative mx-auto w-full max-w-sm" style={{ height: "min(60vh, 500px)" }}>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-10 h-10 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : noMore ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6 rounded-3xl border-2 border-dashed border-white/15 bg-black/30">
                <UsersThree size={64} className="text-white/30 mb-3" weight="duotone" />
                <h3 className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-white mb-1">No hay más perfiles</h3>
                <p className="text-sm text-white/60 mb-4">Vuelve pronto, cada día se unen nuevos jugadores.</p>
                <Button onClick={resetSwipes} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="pm-empty-reset">
                  <ArrowClockwise size={14} className="mr-1" /> Reiniciar swipes
                </Button>
              </div>
            ) : (
              <>
                {next && <SwipeCard candidate={next} zIndex={1} isTop={false} />}
                <SwipeCard candidate={current} zIndex={2} isTop onSwipe={doSwipe} />
              </>
            )}
          </div>

          {!noMore && !loading && (
            <div className="flex items-center justify-center gap-5 pt-2">
              <button
                onClick={() => doSwipe("left", current)}
                className="w-16 h-16 rounded-full bg-rose-500/15 border-2 border-rose-500 text-rose-400 hover:scale-110 active:scale-95 transition-transform flex items-center justify-center shadow-lg"
                data-testid="pm-btn-pass"
                aria-label="Paso"
              >
                <X size={28} weight="bold" />
              </button>
              <button
                onClick={() => doSwipe("right", current)}
                className="w-20 h-20 rounded-full bg-emerald-500/15 border-2 border-emerald-500 text-emerald-400 hover:scale-110 active:scale-95 transition-transform flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.4)]"
                data-testid="pm-btn-like"
                aria-label="Me gusta"
              >
                <Heart size={34} weight="fill" />
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "matches" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {matches.length === 0 ? (
            <div className="col-span-full text-center p-10 border border-dashed border-white/10 rounded-xl">
              <UsersThree size={56} weight="duotone" className="mx-auto text-white/30 mb-2" />
              <p className="text-white/60">Aún no tienes matches. Sigue swipeando y encuentra a tu partner ideal.</p>
            </div>
          ) : matches.map((m) => {
            const u = m.user;
            const avatar = resolvePhoto(u);
            const q = u.quiz || {};
            const hand = { diestro: "Diestro", zurdo: "Zurdo", ambidiestro: "Ambas" }[q.hand];
            const side = { drive: "Drive", reves: "Revés", ambos: "Ambos" }[q.side];
            const freq = { monthly: "1x mes", "1_week": "1x sem", "2_week": "2x sem", "3_week": "3x sem", "4plus_week": "4+ sem" }[q.frequency];
            const years = typeof q.years_playing === "number" ? (q.years_playing === 0 ? "<1a" : `${q.years_playing}+a`) : null;
            return (
              <div key={m.id} className="card-tactical p-4 border border-rose-500/20 hover:border-rose-500/50 transition-colors space-y-3" data-testid={`pm-match-${m.id}`}>
                <div className="flex items-center gap-3">
                  {avatar ? (
                    <img src={avatar} alt={u.name} className="w-20 h-20 rounded-xl object-cover border-2 border-rose-500 shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl border-2 border-rose-500 bg-gradient-to-br from-purple-700 to-pink-700 flex items-center justify-center font-black text-2xl text-white shrink-0">
                      {(u.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-['Barlow_Condensed'] text-2xl font-black uppercase text-white truncate leading-tight">{u.name}</p>
                    <p className="text-xs text-white/60 flex items-center gap-1 flex-wrap">
                      {u.state && <><MapPin size={10} /> {u.state}</>}
                      {u.category && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#CCFF00]/20 text-[#CCFF00] text-[10px] font-bold">C{u.category}</span>}
                    </p>
                    {q.zone && <p className="text-[11px] text-white/50 truncate">Zona: {q.zone}</p>}
                  </div>
                  <button type="button" onClick={() => setChatWith(u)} className="shrink-0" data-testid={`pm-match-chat-${m.id}`}>
                    <span className="inline-flex items-center gap-1 bg-rose-500 text-white hover:bg-rose-600 font-bold uppercase tracking-wide text-xs rounded-md px-3 h-10">
                      <ChatCircle size={14} weight="fill" /> Chat
                    </span>
                  </button>
                </div>
                {(hand || side || freq || years) && (
                  <div className="grid grid-cols-4 gap-1 pt-1">
                    {hand && <div className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-center"><p className="text-[8px] uppercase text-white/50 font-bold tracking-widest">Mano</p><p className="text-[11px] font-black text-white">{hand}</p></div>}
                    {side && <div className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-center"><p className="text-[8px] uppercase text-white/50 font-bold tracking-widest">Lado</p><p className="text-[11px] font-black text-[#CCFF00]">{side}</p></div>}
                    {freq && <div className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-center"><p className="text-[8px] uppercase text-white/50 font-bold tracking-widest">Frec.</p><p className="text-[11px] font-black text-white">{freq}</p></div>}
                    {years && <div className="px-1.5 py-1 rounded bg-black/40 border border-white/10 text-center"><p className="text-[8px] uppercase text-white/50 font-bold tracking-widest">Tiempo</p><p className="text-[11px] font-black text-white">{years}</p></div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <QuizDialog
        open={showQuiz}
        onClose={() => setShowQuiz(false)}
        questions={questions}
        initial={myQuiz || {}}
        onSave={(ans) => { setMyQuiz(ans); loadAll(search); }}
      />
      <MatchModal
        matchUser={matchModal}
        onClose={() => setMatchModal(null)}
        onChat={() => {
          const u = matchModal;
          setMatchModal(null);
          if (u) setChatWith(u);
        }}
      />
      <MatchChatModal
        open={!!chatWith}
        matchUser={chatWith}
        onClose={() => setChatWith(null)}
      />
    </div>
  );
}
